import Illusion from 'illusionjs';
import Model from './Model';

export class Collection {

    protected _items: Model | Model[]

    protected _totalCount: number;

    protected _multiple: boolean
    
    public hasItem: boolean

	constructor(items: Model | Model[], totalCount: number = 0) {

        this._items = items

        this._totalCount = totalCount

        this._multiple = Array.isArray(items)

        this.hasItem = this._multiple ? (items as Model[]).length > 0 : items !== null
    }

    public splice(offset: number, pick: number) {

        if(this._multiple) {

            return this.getItems<Model[]>().splice(offset, pick)
        }
    }

    public itemCount(): number | void {

        if(this._multiple) {
            
            return this.getItems<Model[]>().length
        }
    }

    public totalCount(): number {

        return this._totalCount

    }

    public setTotalCount(count: number): void {
        
        this._totalCount = count
    }

    public getItems<P = Model[] | Model>(): P {

    	return this._items as any
    }

    public first() {

        if(this._multiple) {

            this._items = this.getItems<Model[]>()[0]

            this._multiple = false
        }

        return this
    }

    public valueOf() {
        
        return this.toString()
    }

    public toJSON() {
        
        if(this.hasItem) {

            if (this._multiple) {	

                return this.getItems<Model[]>().map((value: any) => value?.toJSON())
            } else {

                return this.getItems<Model>()?.toJSON()
            }
        } else {

            return this.getItems()
        }
    }

    public toString(): string {

        return JSON.stringify(this.toJSON(), null, 2)
    }

    public reduce(callback: any, defaultValue: any) {

        return [...(this as any)].reduce(callback, defaultValue)
    }

    public map(callback: any) {
        
        return [...(this as any)].map(callback)
    }

    public filter(callback: any) {
        
        return [...(this as any)].filter(callback)
    }

    public forEach(callback: any) {
        
        return [...(this as any)].forEach(callback)
    }

    public __iterator() {

        if(!this.hasItem) {

            return []
        }
        
        return this._multiple ? this.getItems<Model[]>() : this.getItems<Model>().__iterator()
    }

    public __get(key: any): any {

        const items = this.getItems() as any

        if(this.hasItem) {

            const isFunc = !this._multiple && typeof items[key] === 'function'

            return isFunc ? (...parameters: any[]) => items[key](...parameters) : items[key]
        }
    }

    /**
     * Dynamically set items on the model.
     *
     * @param  string  key
     * @param  mixed   value
     * @return void
     */
    public __set(key: string, value: any): void {

        if(!this._multiple && this.hasItem) {

            this.getItems<Model>()[key as keyof Model] = value
        }
    }

    /**
     * Determine if an attribute hasItem on the model.
     *
     * @param  string  key
     * @return bool
     */
    public __isset(key: string): boolean | void {

        if(!this._multiple && this.hasItem) {

            return key in this.getItems<Model>()
        }
    }
    /**
     * Unset an attribute on the model.
     *
     * @param  string  key
     * @return void
     */
    public __unset(key: string): void {
        
        if(!this._multiple && this.hasItem) {
            
            delete this.getItems<Model>()[key as keyof Model]
        }
    }

}

export default Illusion<typeof Collection>(Collection)